import React, { useState, useEffect, useLayoutEffect, useRef, RefObject } from 'react';
import { useCesium } from '../../../../context/CesiumContext';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import styles from './OrbitPanel.module.scss';

interface Props {
  btnRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

const OrbitPanel: React.FC<Props> = ({ btnRef, onClose }) => {
  const cesium = useCesium();
  const { timelineCollapsed, timelineHeight } = useMDCockpit();
  const panelRef = useRef<HTMLDivElement>(null);

  const [leadMin, setLeadMin] = useState(() => Math.round(cesium.orbitLeadTimeRef.current / 60));
  const [trailMin, setTrailMin] = useState(() => Math.round(cesium.orbitTrailTimeRef.current / 60));
  const [pos, setPos] = useState<{ bottom: number; right: number }>({ bottom: 90, right: 60 });

  useLayoutEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    const timelineOffset = timelineCollapsed ? 82 : timelineHeight + 52;
    setPos({
      right: window.innerWidth - rect.left + gap,
      bottom: timelineOffset,
    });
  }, [btnRef, timelineCollapsed, timelineHeight]);

  useEffect(() => {
    setLeadMin(Math.round(cesium.orbitLeadTimeRef.current / 60));
    setTrailMin(Math.round(cesium.orbitTrailTimeRef.current / 60));
  }, [cesium.orbitLeadTimeRef, cesium.orbitTrailTimeRef]);

  // Close only on clicks outside both the panel and the trigger button
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [btnRef, onClose]);

  const handleLead = (min: number) => {
    setLeadMin(min);
    cesium.setOrbitLeadTime(min * 60);
  };

  const handleTrail = (min: number) => {
    setTrailMin(min);
    cesium.setOrbitTrailTime(min * 60);
  };

  const formatWindow = (min: number) => {
    if (min >= 60 && min % 60 === 0) return `${min / 60} hr`;
    return `${min} min`;
  };

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ bottom: pos.bottom, right: pos.right }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <div className={styles.header}>ORBIT DISPLAY</div>

      <div className={styles.row}>
        <span className={styles.lbl}>Lead</span>
        <input
          className={styles.slider}
          type="range"
          min={0}
          max={180}
          step={5}
          value={leadMin}
          onChange={e => handleLead(Number(e.target.value))}
        />
        <span className={styles.val}>{formatWindow(leadMin)}</span>
      </div>

      <div className={styles.row}>
        <span className={styles.lbl}>Tail</span>
        <input
          className={styles.slider}
          type="range"
          min={0}
          max={180}
          step={5}
          value={trailMin}
          onChange={e => handleTrail(Number(e.target.value))}
        />
        <span className={styles.val}>{formatWindow(trailMin)}</span>
      </div>
    </div>
  );
};

export default OrbitPanel;
